import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ChevronRight,
  ArrowLeft,
  CheckCircle2,
  Circle,
  Clock,
  XCircle,
  ChevronDown,
  ListChecks,
  AlertTriangle,
  RefreshCw,
  Layers,
  ShieldAlert,
  ShieldCheck,
  GitBranch,
  PlayCircle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("academiaos_token");

// ── Status configuration ──────────────────────────────────────────────────────

const STATUS_CONFIG = {
  NOT_CONFIGURED: {
    label: "Not Configured",
    color: "text-muted-foreground",
    icon: Circle,
    dot: "bg-muted-foreground",
    badge: "bg-muted text-muted-foreground border-border",
  },
  CONFIGURING: {
    label: "Configuring",
    color: "text-amber-600 dark:text-amber-400",
    icon: Clock,
    dot: "bg-amber-500",
    badge: "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800",
  },
  ACTIVE: {
    label: "Active",
    color: "text-emerald-600 dark:text-emerald-400",
    icon: CheckCircle2,
    dot: "bg-emerald-500",
    badge: "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
  },
  INACTIVE: {
    label: "Inactive",
    color: "text-red-500 dark:text-red-400",
    icon: XCircle,
    dot: "bg-red-500",
    badge: "bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800",
  },
  PENDING: { label: "Pending", color: "text-muted-foreground", icon: Circle, dot: "bg-muted-foreground" },
  ENABLED: { label: "Enabled", color: "text-emerald-600 dark:text-emerald-400", icon: CheckCircle2, dot: "bg-emerald-500" },
  DISABLED: { label: "Disabled", color: "text-red-500", icon: XCircle, dot: "bg-red-400" },
  IN_PROGRESS: { label: "In Progress", color: "text-amber-600 dark:text-amber-400", icon: Clock, dot: "bg-amber-500" },
  COMPLETE: { label: "Complete", color: "text-emerald-600 dark:text-emerald-400", icon: CheckCircle2, dot: "bg-emerald-500" },
  WAIVED: { label: "Waived", color: "text-blue-500", icon: CheckCircle2, dot: "bg-blue-400" },
  BLOCKED: { label: "Blocked", color: "text-red-500", icon: XCircle, dot: "bg-red-400" },
};

const SEVERITY_CONFIG = {
  BLOCKING: {
    label: "BLOCKING",
    bg: "bg-red-50 dark:bg-red-950/30",
    text: "text-red-700 dark:text-red-300",
    border: "border-red-200 dark:border-red-800",
  },
  ERROR: {
    label: "ERROR",
    bg: "bg-orange-50 dark:bg-orange-950/30",
    text: "text-orange-700 dark:text-orange-300",
    border: "border-orange-200 dark:border-orange-800",
  },
  WARNING: {
    label: "WARNING",
    bg: "bg-amber-50 dark:bg-amber-950/20",
    text: "text-amber-700 dark:text-amber-300",
    border: "border-amber-200 dark:border-amber-800",
  },
  INFO: {
    label: "INFO",
    bg: "bg-blue-50 dark:bg-blue-950/20",
    text: "text-blue-700 dark:text-blue-300",
    border: "border-blue-200 dark:border-blue-800",
  },
};

const TASK_TYPE_LABELS = {
  FORM: "Form",
  UPLOAD: "Upload",
  POLICY: "Policy",
  APPROVAL: "Approval",
  REFERENCE_DATA: "Reference Data",
};

// ── Shared micro-components ───────────────────────────────────────────────────

function StatusDot({ status, size = "h-2 w-2" }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.NOT_CONFIGURED;
  return <span className={`inline-block rounded-full ${size} ${cfg.dot}`} />;
}

function StatusLabel({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.NOT_CONFIGURED;
  return <span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span>;
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.NOT_CONFIGURED;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${
        cfg.badge || "bg-muted text-muted-foreground border-border"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ── Task row ──────────────────────────────────────────────────────────────────

function TaskRow({ task }) {
  const isComplete = task.status === "COMPLETE" || task.status === "WAIVED";
  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-lg border transition-colors ${
        isComplete
          ? "border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20"
          : "border-border bg-card hover:bg-muted/30"
      }`}
      data-testid={`setup-task-row-${task.task_code}`}
    >
      <div className="mt-0.5">
        <StatusDot status={task.status} size="h-2 w-2" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`text-sm font-medium ${
              isComplete ? "line-through text-muted-foreground" : "text-foreground"
            }`}
          >
            {task.task_name}
          </span>
          {!task.is_required && (
            <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground border border-border rounded px-1 py-0.5">
              Optional
            </span>
          )}
          {task.task_type && task.task_type !== "FORM" && (
            <span className="text-[10px] font-medium uppercase tracking-wider text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded px-1 py-0.5">
              {TASK_TYPE_LABELS[task.task_type] || task.task_type}
            </span>
          )}
        </div>
        {task.description && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{task.description}</p>
        )}
      </div>
      <StatusLabel status={task.status} />
    </div>
  );
}

// ── Feature collapsible ───────────────────────────────────────────────────────

function FeatureSection({ feature }) {
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const loadTasks = useCallback(async () => {
    const token = getToken();
    if (!token || tasks.length > 0) return;
    setTasksLoading(true);
    try {
      const res = await fetch(`${API}/api/setup/features/${feature.id}/tasks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTasks(data.tasks || []);
      }
    } finally {
      setTasksLoading(false);
    }
  }, [feature.id, tasks.length]);

  const toggle = () => {
    if (!open) loadTasks();
    setOpen((prev) => !prev);
  };

  const taskComplete = tasks.filter(
    (t) => t.status === "COMPLETE" || t.status === "WAIVED"
  ).length;

  return (
    <div
      className="border border-border rounded-xl overflow-hidden"
      data-testid={`setup-feature-${feature.feature_code}`}
    >
      <button
        onClick={toggle}
        className="w-full flex items-center gap-3 px-4 py-3 bg-card hover:bg-muted/30 transition-colors text-left"
        data-testid={`setup-feature-toggle-${feature.feature_code}`}
      >
        <ListChecks className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-foreground">{feature.feature_name}</span>
            {feature.is_optional && (
              <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground border border-border rounded px-1 py-0.5">
                Optional
              </span>
            )}
          </div>
          {feature.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{feature.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusDot status={feature.status} size="h-1.5 w-1.5" />
          <StatusLabel status={feature.status} />
          {tasks.length > 0 && (
            <span className="text-xs text-muted-foreground ml-1">
              {taskComplete}/{tasks.length}
            </span>
          )}
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-2 bg-muted/10 border-t border-border space-y-2">
          {tasksLoading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
              <RefreshCw className="h-3 w-3 animate-spin" /> Loading tasks…
            </div>
          )}
          {!tasksLoading && tasks.length === 0 && (
            <p className="text-xs text-muted-foreground py-2">No tasks defined for this feature.</p>
          )}
          {tasks.map((t) => (
            <TaskRow key={t.id} task={t} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Functional area collapsible ───────────────────────────────────────────────

function FunctionalAreaCard({ area }) {
  const [features, setFeatures] = useState([]);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const loadFeatures = useCallback(async () => {
    const token = getToken();
    if (!token || features.length > 0) return;
    setFeaturesLoading(true);
    try {
      const res = await fetch(`${API}/api/setup/functional-areas/${area.id}/features`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFeatures(data.features || []);
      }
    } finally {
      setFeaturesLoading(false);
    }
  }, [area.id, features.length]);

  const toggle = () => {
    if (!open) loadFeatures();
    setOpen((prev) => !prev);
  };

  return (
    <div
      className="rounded-xl border border-border bg-card overflow-hidden"
      data-testid={`setup-area-card-${area.area_code}`}
    >
      <button
        onClick={toggle}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-muted/30 transition-colors text-left"
        data-testid={`setup-area-toggle-${area.area_code}`}
      >
        <div
          className="flex h-8 w-8 items-center justify-center rounded-lg shrink-0"
          style={{ background: "hsl(var(--primary)/0.12)", color: "hsl(var(--primary))" }}
        >
          <Layers className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">{area.area_name}</p>
          {area.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{area.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {features.length > 0 && (
            <span className="text-xs text-muted-foreground">{features.length} features</span>
          )}
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 bg-muted/10 border-t border-border space-y-3">
          {featuresLoading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
              <RefreshCw className="h-3 w-3 animate-spin" /> Loading features…
            </div>
          )}
          {!featuresLoading && features.length === 0 && (
            <p className="text-xs text-muted-foreground py-3">No features defined for this area.</p>
          )}
          {features.map((f) => (
            <FeatureSection key={f.id} feature={f} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── SS02: Configuration & Activation Status Panel ────────────────────────────

function ConfigStatusSection({
  domain,
  blockers,
  validation,
  onValidate,
  onActivate,
  activating,
  validating,
}) {
  const isActive = domain.status === "ACTIVE";
  const isInactive = domain.status === "INACTIVE";
  const canTransitionToConfiguring = domain.status === "NOT_CONFIGURED";
  const canActivate = domain.status === "CONFIGURING";

  // Derive activation status from blockers.
  // Activation status is only meaningful when the domain is CONFIGURING;
  // NOT_CONFIGURED domains show '—' because activation requires starting config first.
  const activationStatus = isActive
    ? "ACTIVE"
    : isInactive
    ? "INACTIVE"
    : domain.status === "CONFIGURING"
    ? blockers
      ? blockers.blocking_count > 0
        ? "BLOCKED"
        : "READY"
      : null
    : null;

  const blockingCount = blockers?.blocking_count ?? 0;

  // Non-dependency validation issues (warnings, task incomplete, etc.)
  const validationIssues =
    validation?.issues?.filter((i) => i.code !== "DEPENDENCY_NOT_SATISFIED") ?? [];

  return (
    <div
      className="rounded-xl border border-border bg-card p-5 mb-6"
      data-testid="config-status-section"
    >
      {/* ── Row 1: Status labels ── */}
      <div className="flex items-start gap-8 flex-wrap">
        {/* Configuration Status */}
        <div data-testid="config-status-label">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
            Configuration Status
          </p>
          <StatusBadge status={domain.status} />
        </div>

        {/* Activation Status */}
        <div data-testid="activation-status-label">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
            Activation Status
          </p>
          {activationStatus === "ACTIVE" && (
            <span
              data-testid="activation-status-active"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Active
            </span>
          )}
          {activationStatus === "BLOCKED" && (
            <span
              data-testid="activation-status-blocked"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
              Blocked
            </span>
          )}
          {activationStatus === "READY" && (
            <span
              data-testid="activation-status-ready"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
              Ready to Activate
            </span>
          )}
          {activationStatus === "INACTIVE" && (
            <span
              data-testid="activation-status-inactive"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border bg-muted text-muted-foreground border-border"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
              Inactive
            </span>
          )}
          {!activationStatus && (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </div>

        {/* Blocking Issues count */}
        {blockingCount > 0 && (
          <div data-testid="blocking-count-stat">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
              Blocking Issues
            </p>
            <p className="text-sm font-bold text-red-600 dark:text-red-400">{blockingCount}</p>
          </div>
        )}

        {/* Validation Warnings count */}
        {validationIssues.length > 0 && (
          <div data-testid="validation-issue-count-stat">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
              Validation Warnings
            </p>
            <p className="text-sm font-bold text-amber-600 dark:text-amber-400">
              {validationIssues.length}
            </p>
          </div>
        )}
      </div>

      {/* ── Divider ── */}
      <div className="h-px bg-border my-4" />

      {/* ── Row 2: Action buttons ── */}
      <div className="flex items-start gap-3 flex-wrap">
        {/* Validate Configuration — always available */}
        <button
          data-testid="validate-config-btn"
          onClick={onValidate}
          disabled={validating}
          className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-border bg-muted hover:bg-muted/70 text-foreground transition-colors disabled:opacity-60"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${validating ? "animate-spin" : ""}`} />
          {validating ? "Validating…" : "Validate Configuration"}
        </button>

        {/* Start Configuring */}
        {canTransitionToConfiguring && (
          <button
            data-testid="start-configuring-btn"
            onClick={() => onActivate("CONFIGURING")}
            disabled={activating}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border bg-primary text-primary-foreground border-primary hover:opacity-90 transition-colors disabled:opacity-60"
          >
            <PlayCircle className="h-3.5 w-3.5" />
            {activating ? "…" : "Start Configuring"}
          </button>
        )}

        {/* Activate (CONFIGURING → ACTIVE) */}
        {canActivate && (
          <div className="flex flex-col gap-0.5">
            <button
              data-testid="activate-domain-btn"
              onClick={() => onActivate("ACTIVE")}
              disabled={blockingCount > 0 || activating}
              title={
                blockingCount > 0
                  ? `Resolve ${blockingCount} blocking ${
                      blockingCount === 1 ? "issue" : "issues"
                    } before activating`
                  : "Activate this domain"
              }
              className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                blockingCount > 0
                  ? "bg-muted text-muted-foreground border-border cursor-not-allowed opacity-60"
                  : "bg-primary text-primary-foreground border-primary hover:opacity-90"
              }`}
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              {activating ? "Activating…" : "Activate"}
            </button>
            {blockingCount > 0 && (
              <p
                className="text-[10px] text-muted-foreground leading-snug"
                data-testid="activate-blocked-hint"
              >
                Resolve {blockingCount} blocking{" "}
                {blockingCount === 1 ? "issue" : "issues"} before activating.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── SS02: Dependencies Section ────────────────────────────────────────────────

function DependencyRow({ dep }) {
  const satisfied = dep.is_satisfied;
  const targetTypeLabel = dep.target_type.replace(/_/g, " ");

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${
        satisfied
          ? "border-emerald-200 dark:border-emerald-800 bg-emerald-50/30 dark:bg-emerald-950/10"
          : "border-red-200 dark:border-red-800 bg-red-50/30 dark:bg-red-950/10"
      }`}
      data-testid={`dep-row-${dep.dependency_id}`}
    >
      <div className="mt-0.5 shrink-0">
        {satisfied ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        ) : (
          <XCircle className="h-4 w-4 text-red-500 dark:text-red-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-foreground capitalize">
            {dep.target_name || dep.target_id}
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-wider border border-border rounded px-1.5 py-0.5 text-muted-foreground">
            {dep.dependency_type}
          </span>
          <span className="text-[10px] uppercase tracking-wider border border-border rounded px-1.5 py-0.5 text-muted-foreground">
            {targetTypeLabel}
          </span>
        </div>
        <p
          className={`text-xs mt-0.5 ${
            satisfied
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-red-600 dark:text-red-400"
          }`}
          data-testid={`dep-status-${dep.dependency_id}`}
        >
          Status:{" "}
          {satisfied
            ? "Satisfied"
            : `Missing — current: ${dep.target_current_status || "NOT FOUND"}`}
        </p>
        {!satisfied && dep.failure_message && (
          <p className="text-xs text-muted-foreground mt-1 leading-snug">
            {dep.failure_message}
          </p>
        )}
      </div>
      {dep.is_required === false && (
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground border border-border rounded px-1 py-0.5 shrink-0 self-start mt-0.5">
          Optional
        </span>
      )}
    </div>
  );
}

function DependenciesSection({ deps }) {
  if (!deps || deps.length === 0) return null;
  const satisfied = deps.filter((d) => d.is_satisfied).length;
  const unsatisfied = deps.filter((d) => !d.is_satisfied).length;

  return (
    <div className="mb-6" data-testid="dependencies-section">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold text-foreground">Dependencies</h2>
        <span className="text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">
          {deps.length}
        </span>
        {satisfied > 0 && (
          <span
            className="text-xs text-emerald-600 dark:text-emerald-400"
            data-testid="deps-satisfied-count"
          >
            {satisfied} satisfied
          </span>
        )}
        {unsatisfied > 0 && (
          <span
            className="text-xs text-red-500 dark:text-red-400"
            data-testid="deps-unsatisfied-count"
          >
            {unsatisfied} unresolved
          </span>
        )}
        <div className="flex-1 h-px bg-border ml-1" />
      </div>
      <div className="space-y-2">
        {deps.map((dep) => (
          <DependencyRow key={dep.dependency_id} dep={dep} />
        ))}
      </div>
    </div>
  );
}

// ── SS02: Validation Section ──────────────────────────────────────────────────

function ValidationIssueRow({ issue, idx }) {
  const sev = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.INFO;
  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${sev.border} ${sev.bg}`}
      data-testid={`validation-issue-${idx}`}
    >
      <span
        className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 mt-0.5 ${sev.text} ${sev.border}`}
      >
        {sev.label}
      </span>
      <p className={`text-xs leading-snug ${sev.text}`}>{issue.message}</p>
    </div>
  );
}

function ValidationSection({ validation }) {
  if (!validation) return null;
  // Dependency issues are shown in DependenciesSection; exclude them here
  const issues =
    validation.issues?.filter((i) => i.code !== "DEPENDENCY_NOT_SATISFIED") ?? [];
  if (issues.length === 0) return null;

  return (
    <div className="mb-6" data-testid="validation-section">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold text-foreground">Validation</h2>
        <span className="text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">
          {issues.length}
        </span>
        <div className="flex-1 h-px bg-border ml-1" />
      </div>
      <div className="space-y-2">
        {issues.map((issue, idx) => (
          <ValidationIssueRow key={idx} issue={issue} idx={idx} />
        ))}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SetupDomainDetail() {
  const { domainId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [domain, setDomain] = useState(location.state?.domain || null);
  const [areas, setAreas] = useState([]);
  const [evaluatedDeps, setEvaluatedDeps] = useState([]);
  const [blockers, setBlockers] = useState(null);
  const [validation, setValidation] = useState(null);
  const [activating, setActivating] = useState(false);
  const [validating, setValidating] = useState(false);
  const [activationError, setActivationError] = useState(null);
  const [activationSuccess, setActivationSuccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load all domain data including SS02 evaluated dependencies
  const fetchAll = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    setError(null);
    setActivationError(null);
    setActivationSuccess(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [domainRes, areasRes, blockersRes, validRes, depsRes] = await Promise.all([
        fetch(`${API}/api/setup/domains/${domainId}`, { headers }),
        fetch(`${API}/api/setup/domains/${domainId}/functional-areas`, { headers }),
        fetch(`${API}/api/setup/domains/${domainId}/blockers`, { headers }),
        fetch(`${API}/api/setup/domains/${domainId}/validate`, {
          method: "POST",
          headers,
        }),
        fetch(`${API}/api/setup/domains/${domainId}/dependencies-evaluated`, { headers }),
      ]);

      if (!domainRes.ok) {
        const e = await domainRes.json().catch(() => ({}));
        throw new Error(e.detail?.message || e.detail || "Domain not found");
      }
      setDomain(await domainRes.json());

      if (areasRes.ok) {
        const data = await areasRes.json();
        setAreas(data.functional_areas || []);
      }
      if (blockersRes.ok) setBlockers(await blockersRes.json());
      if (validRes.ok) setValidation(await validRes.json());
      if (depsRes.ok) {
        const data = await depsRes.json();
        setEvaluatedDeps(data.dependencies || []);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [domainId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Manual "Validate Configuration" — re-runs blockers + validation without reloading areas
  const handleValidate = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setValidating(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [blockersRes, validRes, depsRes] = await Promise.all([
        fetch(`${API}/api/setup/domains/${domainId}/blockers`, { headers }),
        fetch(`${API}/api/setup/domains/${domainId}/validate`, {
          method: "POST",
          headers,
        }),
        fetch(`${API}/api/setup/domains/${domainId}/dependencies-evaluated`, { headers }),
      ]);
      if (blockersRes.ok) setBlockers(await blockersRes.json());
      if (validRes.ok) setValidation(await validRes.json());
      if (depsRes.ok) {
        const data = await depsRes.json();
        setEvaluatedDeps(data.dependencies || []);
      }
    } finally {
      setValidating(false);
    }
  }, [domainId]);

  // Status transition (CONFIGURING or ACTIVE)
  const handleStatusChange = async (nextStatus) => {
    const token = getToken();
    if (!token || !domain) return;
    setActivating(true);
    setActivationError(null);
    setActivationSuccess(null);
    try {
      const res = await fetch(`${API}/api/setup/domains/${domainId}/status`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: nextStatus }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail || {};
        setActivationError({
          type: detail.error || "error",
          message: detail.message || "Action failed",
          blockers: detail.blockers || [],
        });
      } else {
        setActivationSuccess(`Domain moved to ${data.status}`);
        await fetchAll();
      }
    } catch (e) {
      setActivationError({ type: "error", message: e.message, blockers: [] });
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="setup-domain-detail-page">
      {/* ── Header ── */}
      <div className="border-b border-border bg-card px-6 py-4">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
            <Link
              to="/setup"
              className="hover:text-foreground transition-colors"
              data-testid="setup-breadcrumb-home"
            >
              Setup Studio
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-foreground font-medium">
              {domain?.domain_name || "Domain"}
            </span>
          </nav>

          {/* Title row */}
          <div className="flex items-start gap-4">
            <button
              data-testid="setup-back-btn"
              onClick={() => navigate("/setup")}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mt-0.5 shrink-0"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </button>
            {domain && (
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1
                    className="text-xl font-bold text-foreground"
                    data-testid="setup-domain-detail-title"
                  >
                    {domain.domain_name}
                  </h1>
                  <StatusBadge status={domain.status} />
                </div>
                {domain.description && (
                  <p className="text-xs text-muted-foreground mt-0.5">{domain.description}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Page-level error */}
        {error && (
          <div
            className="flex items-center gap-3 p-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 mb-6"
            data-testid="setup-domain-error"
          >
            <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            <button onClick={fetchAll} className="ml-auto text-xs text-red-600 underline">
              Retry
            </button>
          </div>
        )}

        {/* Activation result banners */}
        {activationSuccess && (
          <div
            className="flex items-center gap-2 p-3 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 mb-4"
            data-testid="activation-success-banner"
          >
            <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
            <p className="text-sm text-emerald-700 dark:text-emerald-300">
              {activationSuccess}
            </p>
          </div>
        )}
        {activationError && (
          <div
            className="p-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 mb-4"
            data-testid="activation-error-banner"
          >
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert className="h-4 w-4 text-red-500 shrink-0" />
              <p className="text-sm font-medium text-red-700 dark:text-red-300">
                {activationError.message}
              </p>
            </div>
            {activationError.blockers?.length > 0 && (
              <ul className="mt-2 space-y-1.5 pl-1">
                {activationError.blockers.map((b, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-xs text-red-600 dark:text-red-400"
                    data-testid={`activation-blocker-${i}`}
                  >
                    <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>{b.message}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* ── SS02: Configuration Status Section ── */}
        {!loading && domain && (
          <ConfigStatusSection
            domain={domain}
            blockers={blockers}
            validation={validation}
            onValidate={handleValidate}
            onActivate={handleStatusChange}
            activating={activating}
            validating={validating}
          />
        )}

        {/* ── SS02: Dependencies Section ── */}
        {!loading && evaluatedDeps.length > 0 && (
          <DependenciesSection deps={evaluatedDeps} />
        )}

        {/* ── SS02: Validation Section ── */}
        {!loading && <ValidationSection validation={validation} />}

        {/* Loading skeleton */}
        {loading && (
          <div className="space-y-4" data-testid="setup-domain-loading">
            <div className="h-32 rounded-xl border border-border bg-muted/30 animate-pulse" />
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-16 rounded-xl border border-border bg-muted/30 animate-pulse"
              />
            ))}
          </div>
        )}

        {/* ── Functional Areas ── */}
        {!loading && !error && areas.length === 0 && (
          <div
            className="flex flex-col items-center justify-center py-16 text-center"
            data-testid="setup-areas-empty"
          >
            <Layers className="h-10 w-10 text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium text-muted-foreground">
              No functional areas configured
            </p>
          </div>
        )}

        {!loading && !error && areas.length > 0 && (
          <>
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-sm font-semibold text-foreground">Functional Areas</h2>
              <span className="text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">
                {areas.length}
              </span>
              <div className="flex-1 h-px bg-border ml-1" />
            </div>
            <div className="space-y-4" data-testid="setup-areas-list">
              {areas.map((area) => (
                <FunctionalAreaCard key={area.id} area={area} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
